import React from 'react';
import { MessageCircle, Loader } from 'lucide-react';
import { getRecommendationBadge } from '../utils/reviewPrioritization';

function ReviewList({ reviews, selectedReview, onSelectReview, loading }) {
  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-400';
    if (rating >= 3) return 'text-yellow-400';
    return 'text-red-400';
  };

  const getRatingBgColor = (rating) => {
    if (rating >= 4) return 'bg-green-500/20';
    if (rating >= 3) return 'bg-yellow-500/20';
    return 'bg-red-500/20';
  };

  return (
    <div className="bg-slate-800/50 backdrop-blur-sm border border-purple-500/20 rounded-2xl shadow-2xl p-8">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">
        <MessageCircle className="text-cyan-400" size={28} />
        Recent Reviews
      </h2>

      {loading ? (
        <div className="flex justify-center items-center h-96">
          <div className="flex flex-col items-center gap-3">
            <Loader className="h-12 w-12 text-purple-400 animate-spin" />
            <p className="text-purple-300">Loading reviews...</p>
          </div>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-16">
          <MessageCircle className="w-12 h-12 text-purple-400/30 mx-auto mb-3" />
          <p className="text-purple-300/60 text-lg">No reviews found</p>
          <p className="text-purple-300/40 text-sm mt-2">Click "Refresh Reviews" to load reviews from Facebook</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
          {reviews.map((review) => (
            <div
              key={review.id}
              onClick={() => onSelectReview(review)}
              className={`p-4 rounded-xl cursor-pointer transition-all border backdrop-blur-sm ${
                selectedReview?.id === review.id
                  ? 'bg-gradient-to-r from-purple-600/40 to-pink-600/40 border-purple-500/60 shadow-lg shadow-purple-500/20 ring-1 ring-purple-500/30'
                  : 'bg-slate-700/30 border-purple-500/20 hover:border-purple-500/40 hover:bg-slate-700/40'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                  selectedReview?.id === review.id
                    ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                }`}>
                  {getRecommendationBadge(review).label.charAt(0)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-semibold text-white text-sm truncate">
                      {review.reviewer_name || 'Anonymous'}
                    </h3>
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap"
                      title={getRecommendationBadge(review).description}
                    >
                      {getRecommendationBadge(review).label}
                    </span>
                    {review.platform_source === 'facebook' && (
                      <span className="inline-block bg-blue-500/30 text-blue-200 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                        📘 FB
                      </span>
                    )}
                    {(!review.platform_source || review.platform_source === 'local') && (
                      <span className="inline-block bg-gray-500/30 text-gray-300 text-xs font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                        📱 Local
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-purple-300/50">
                    {new Date(review.review_posted_at || review.published_at).toLocaleDateString()}
                  </p>
                </div>
                <div className={`${getRatingBgColor(review.rating)} ${getRatingColor(review.rating)} px-3 py-1 rounded-full font-bold text-sm flex-shrink-0 whitespace-nowrap`}>
                  ⭐ {review.rating}/5
                </div>
              </div>

              <p className="text-purple-100 text-sm leading-relaxed line-clamp-2">
                {review.text}
              </p>

              {review.has_reply && (
                <div className="mt-2 flex items-center text-green-400 text-xs font-semibold">
                  ✓ Already replied
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ReviewList;
